import React, { useState, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import isEqual from 'lodash/isEqual';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import './PillInput.scss';

interface PillInputProps {
    pills: string[];
    onPillsChanged: (pills: string[]) => void;
    onEnter?: () => void;
    onTab?: () => void;
    onInputLoseFocus?: () => void;
    updateInputText?: (inputText: string, confirmedPills: string[]) => string;
    placeholder?: string | string[];
    noInputTextOnBackspaceRemovePill?: boolean;
    hideInputPillCount?: number;
    onEditing?: (index?: number) => void;
    confirmOnChange?: boolean;
    renderInput?: (
        props: React.DetailedHTMLProps<
            React.InputHTMLAttributes<HTMLInputElement>,
            HTMLInputElement
        >
    ) => React.ReactNode;
    renderInputs?: Array<
        (
            props: React.DetailedHTMLProps<
                React.InputHTMLAttributes<HTMLInputElement>,
                HTMLInputElement
            >
        ) => React.ReactNode
    >;
}

export default function PillInput(props: PillInputProps) {
    const {
        pills,
        onPillsChanged: onParentPillsChanged,
        onEnter,
        onTab,
        onInputLoseFocus,
        updateInputText,
        placeholder,
        noInputTextOnBackspaceRemovePill,
        hideInputPillCount,
        renderInput: renderInputProps,
        renderInputs,
        onEditing,
        confirmOnChange,
    } = props;

    // Values shown as pills (initially set to props received from parent)...
    const [confirmedPills, setConfirmedPills] = useState(pills);
    // "Pending" pill in input...
    const [pending, setPending] = useState('');

    const updatePending = (newPending: string) =>
        setPending(
            updateInputText
                ? updateInputText(newPending, confirmedPills)
                : newPending
        );

    useEffect(() => {
        const exposedPills = [...confirmedPills, pending];
        if (!isEqual(pills, exposedPills)) {
            // We need to update our pills if the received props doesn't match confirmed + pending...
            setConfirmedPills(pills);
        }
    }, [pills, confirmedPills, pending]);

    const displayInput =
        hideInputPillCount === undefined ||
        confirmedPills.length < hideInputPillCount;

    useEffect(() => {
        if (onEditing) {
            if (displayInput) {
                onEditing(confirmedPills.length);
            } else {
                onEditing();
            }
        }
    }, [onEditing, displayInput, confirmedPills.length]);

    const onPillsChanged = (pills: string[]) => {
        onParentPillsChanged(pills.filter((p) => !!p));
    };

    const onInputBlur = () => {
        // When leave the input, set pending to blank so the pending value becomes confirmed in the effect...
        if (pending !== '') {
            if (onInputLoseFocus) {
                onInputLoseFocus();
            }
            updatePending('');
        }
    };

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        // Update pending and notify parent as soon as input is changed...
        updatePending(event.target.value);
        onPillsChanged([...confirmedPills, event.target.value]);
        if (confirmOnChange) {
            updatePending('');
        }
    };

    const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && pending !== '') {
            event.preventDefault();
            // On tab pending to blank so the pending value becomes confirmed in the effect...
            if (onTab) {
                onTab();
            }
            updatePending('');
        }

        if (event.key === 'Enter') {
            if (onEnter) {
                onEnter();
            }
            updatePending('');
        }

        if (event.key === 'Backspace' && pending === '') {
            if (!confirmedPills.length) {
                return;
            }

            const updatedPills = [...confirmedPills];
            const nextPending = updatedPills.pop()!;
            setConfirmedPills(updatedPills);

            if (noInputTextOnBackspaceRemovePill) {
                onPillsChanged([...updatedPills, pending]);
            } else {
                // This makes the last pill the pending input value...
                updatePending(nextPending);
                onPillsChanged([...updatedPills, nextPending]);
            }
        }
    };

    const onRemovePill = (index: number) => {
        confirmedPills.splice(index, 1);
        setConfirmedPills(confirmedPills);
        onPillsChanged([...confirmedPills]);
    };

    const pillIndex = confirmedPills.length;
    let renderInput = renderInputProps ?? defaultRenderInput;
    if (renderInputs && renderInputs[pillIndex]) {
        renderInput = renderInputs[pillIndex]!;
    }

    return (
        <div className="ui input PillInput">
            <ul className="pills">
                {confirmedPills.map((p, i) => (
                    <li key={i} className="pill">
                        {p}
                        <div
                            className="pill-delete"
                            onClick={() => onRemovePill(i)}
                        >
                            <Icon className="action" name="close" />
                        </div>
                    </li>
                ))}

                {displayInput && (
                    <li className="pending">
                        {renderInput({
                            value: pending,
                            placeholder: getPlaceholderText(
                                placeholder,
                                confirmedPills.length
                            ),
                            onKeyDown: onInputKeyDown,
                            onBlur: onInputBlur,
                            onChange: onInputChange,
                        })}
                    </li>
                )}
            </ul>
        </div>
    );

    type DefaultRenderInputProps = React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    >;

    function defaultRenderInput(
        props: DefaultRenderInputProps
    ): React.ReactNode {
        const { value, placeholder, onKeyDown, onBlur, onChange } = props;
        return (
            <input
                data-testid="pill-input"
                value={value}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                onChange={onChange}
                autoFocus={true}
            />
        );
    }
}

function getPlaceholderText(
    placeholder: string | string[] | undefined,
    confirmedPillsCount: number
) {
    return Array.isArray(placeholder)
        ? getPlaceholderTextFromArray(placeholder, confirmedPillsCount)
        : placeholder;
}

function getPlaceholderTextFromArray(
    placeholder: string[],
    confirmedPillsCount: number
) {
    return confirmedPillsCount < placeholder.length
        ? placeholder[confirmedPillsCount]
        : undefined;
}
